"use client";

import { Suspense, useEffect } from "react";
import { usePathname, useSearchParams } from "next/navigation";

import { GTMEvents, GTMSubEvents, PushDataToGTM } from "@/lib/gtm";
import { gtmPageAnalyticsFromPathname } from "@/lib/gtm/pageContext";
import { resolveSelectedNavCityClient } from "@/lib/navCityCookie";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { setPageAnalytics } from "@/store/globalSlice";

function PageViewTrackerInner() {
  const pathname = usePathname() ?? "/";
  const searchParams = useSearchParams();
  const dispatch = useAppDispatch();
  const pageVisited = useAppSelector((s) => s.global.pageVisited);
  const lastTrackedUrl = useAppSelector((s) => s.global.lastTrackedUrl);

  useEffect(() => {
    const search = searchParams?.toString() ?? "";
    const query = search ? `?${search}` : "";
    const url = search ? `${pathname}${query}` : pathname;

    if (url === lastTrackedUrl) return;

    const navCity = resolveSelectedNavCityClient();
    const city = navCity?.nameEn?.trim() ? navCity.nameEn.toUpperCase() : "";

    const { pageName, pageCategory, pageTitle } = gtmPageAnalyticsFromPathname(
      pathname,
      query
    );

    const eventCategory = pageVisited
      ? GTMSubEvents.SpaPageView
      : GTMSubEvents.PageView;

    PushDataToGTM(GTMEvents.DataLayerUpdate, {
      eventCategory: eventCategory?.toUpperCase(),
      pageCategory: pageCategory?.toUpperCase(),
      pageName: pageName?.toUpperCase(),
      originalLocation: window.location.href,
      pageTitle: pageTitle?.toUpperCase(),
      city: city?.toUpperCase(),
      url,
    });

    dispatch(setPageAnalytics(url));
  }, [pathname, searchParams, dispatch, pageVisited, lastTrackedUrl]);

  return null;
}

export function PageViewTracker() {
  return (
    <Suspense fallback={null}>
      <PageViewTrackerInner />
    </Suspense>
  );
}
